import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { PipelineStatus, PipelineState } from '../types/airflow';
import { getPipelineStatus } from '../utils/airflowApi';
import { queryKeys } from '../lib/queryClient';

interface UsePipelineStatusOptions {
  dagId: string;
  runId: string;
  onStatusChange?: (state: PipelineState) => void;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export const usePipelineStatus = ({
  dagId,
  runId,
  onStatusChange,
  autoRefresh = true,
  refreshInterval = 30000, // 30 seconds default
}: UsePipelineStatusOptions) => {
  // Track whether we should poll based on pipeline state
  const [shouldPoll, setShouldPoll] = React.useState(true);

  const query = useQuery({
    queryKey: queryKeys.pipelineStatus(dagId, runId),
    queryFn: () => getPipelineStatus(dagId, runId),
    enabled: Boolean(dagId && runId),
    // Only poll if autoRefresh is true AND pipeline is not in final state
    refetchInterval: autoRefresh && shouldPoll ? refreshInterval : false,
    // Don't refetch in background when window is inactive
    refetchIntervalInBackground: false,
    // Don't auto-refetch on window focus - let interval handle it
    refetchOnWindowFocus: false,
    // Don't retry on errors
    retry: false,
    // Keep previous data while refetching
    placeholderData: (previousData: PipelineStatus | undefined) => previousData,
  });

  // Call status change callback when data changes
  React.useEffect(() => {
    if (query.data?.state && onStatusChange) {
      onStatusChange(query.data.state as PipelineState);
    }
  }, [query.data?.state, onStatusChange]);

  // Update polling state based on pipeline state
  React.useEffect(() => {
    if (query.data?.state) {
      const isFinal = isInFinalState(query.data.state);
      setShouldPoll(!isFinal);
    }
  }, [query.data?.state]);

  return {
    ...query,
    status: query.data as PipelineStatus | undefined,
    isPolling: shouldPoll && autoRefresh && !query.isPaused,
  };
};

// Helper function to check if pipeline is in final state
const isInFinalState = (state?: string) => {
  if (!state) return false;
  
  const finalStates = [
    PipelineState.SUCCESS,
    PipelineState.FAILED,
    PipelineState.SKIPPED,
    PipelineState.UPSTREAM_FAILED
  ];
  return finalStates.includes(state as PipelineState);
};